import { z } from 'zod';
import type { TestResult } from './project.js';

export const SIGNOFF_TERMS = ['bestaetigung', 'abnahme'] as const;
export const SignoffTermSchema = z.enum(SIGNOFF_TERMS);
export type SignoffTerm = z.infer<typeof SignoffTermSchema>;

export const SignoffEvidenceSchema = z.object({
  passed_run_count: z.number().int(),
  total_in_scope: z.number().int(),
});
export type SignoffEvidence = z.infer<typeof SignoffEvidenceSchema>;

export const SignoffSchema = z.object({
  signoff_id: z.string().uuid(),
  node_id: z.string().uuid(),
  node_name: z.string(),
  confirmer_user_id: z.string().uuid(),
  confirmer_name: z.string(),
  confirmed_at: z.string().datetime(),
  note: z.string().nullable(),
  evidence: SignoffEvidenceSchema,
  revoked_at: z.string().datetime().nullable(),
});
export type Signoff = z.infer<typeof SignoffSchema>;

// #88: Ein Widerruf braucht einen Grund — sonst ist später nicht nachvollziehbar, warum eine
// Bestätigung fiel. Getrimmt, damit Leerzeichen nicht als Grund durchgehen; gedeckelt wie ein Kommentar.
export const RevokeSignoffInputSchema = z.object({
  reason: z.string().trim().min(1).max(2000),
});
export type RevokeSignoffInput = z.infer<typeof RevokeSignoffInputSchema>;

// #83 (Task 6): die Prozess-Herkunft eines Abnahme-Knotens — WARUM zählt hier ein Testfall mit?
// Kette Testfall → Prozess → process_labels → Knoten (siehe computeProcessOrigins). Reiner
// Transport-Typ wie UnassignedInScope: der Server berechnet ihn, der Client liest ihn nur.
// Whole-Branch-Review (Minor): `count` war bis eben ein eigenes Feld, aber per Konstruktion IMMER
// gleich `processes.length` (computeProcessOrigins zählt und pusht im selben Schritt, keine
// serverseitige Kappung) — zwei Felder für eine Zahl sind der Anfang von zwei Zahlen, sobald mal
// nur eines der beiden angepasst wird. Gestrichen statt im Typ festgehalten: die Kappung
// (PROCESS_ORIGIN_LIMIT) passiert ausschließlich am Client beim Rendern (signoff-page.tsx), der
// Server kappt nie — es gibt also keine "ungekappte Gesamtzahl", die `processes.length` nicht
// bereits wäre.
export interface ProcessOriginItem {
  process_id: string;
  name_de: string;
  name_en: string;
}
// #112: Eine Kette als Herkunft eines Stream-Knotens. Bewusst ein eigenes Feld statt die Ketten
// unter `processes` mitzuführen — das wären zwei Dinge unter einem Namen, und die Herkunftszeile
// soll sagen können „über die Kette ETO-E2E-001", nicht „über den Prozess ETO-E2E-001".
export interface E2eOriginItem {
  e2e_id: string;
  human_id: string;
  name_de: string;
  name_en: string;
}
export interface ProcessOrigin {
  processes: ProcessOriginItem[];
  // Nur bei Knoten vom Typ `stream` gefüllt; bei Teilprojekten immer leer (und umgekehrt).
  e2e_processes: E2eOriginItem[];
}

// #34 (EXE/SGN): In-Scope-Testfälle, die an keinen Sign-off-Knoten gebunden sind (siehe
// computeUnassignedInScope). Reine Transport-Typen — kein Zod-Runtime nötig, da nur Server → Client
// (die Route erzeugt sie, der Client liest sie).
export interface UnassignedTestCase {
  test_case_id: string;
  human_id: string;
  title_de: string;
  title_en: string;
  // #34: das ECHTE jüngste Ergebnis (nicht nur ein boolean passed) — ein fehlgeschlagener Testfall
  // darf in der Abnahme-Transparenz nicht als „offen" erscheinen. null = noch nie ausgeführt.
  latest_result: TestResult | null;
}
export interface UnassignedInScope {
  count: number;
  passed_count: number;
  test_cases: UnassignedTestCase[];
}

export const CreateSignoffInputSchema = z.object({
  node_id: z.string().uuid(),
  // #55: der Testplan kommt ab jetzt immer explizit von außen — die Route rät ihn nicht mehr
  // implizit über is_active.
  test_plan_id: z.string().uuid(),
  note: z.string().max(2000).optional(),
});
export type CreateSignoffInput = z.infer<typeof CreateSignoffInputSchema>;
